import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Trash2 } from 'lucide-react';

export const ResourceDialog = ({ open, onOpenChange, resource, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    name: '',
    avatar: '',
    csr: ''
  });

  useEffect(function() {
    if (resource) {
      setFormData({
        name: resource.name,
        avatar: resource.avatar || '',
        csr: resource.csr !== undefined && resource.csr !== null ? String(resource.csr) : ''
      });
    } else {
      setFormData({
        name: '',
        avatar: '',
        csr: ''
      });
    }
  }, [resource, open]);

  const handleSubmit = function(e) {
    e.preventDefault();
    if (!formData.name) return;
    
    const resourceData = {
      name: formData.name,
      avatar: formData.avatar,
      csr: formData.csr === '' ? null : Number(formData.csr)
    };
    
    if (resource && resource.id) {
      resourceData.id = resource.id;
    }
    
    onSave(resourceData);
    onOpenChange(false);
  };

  const handleDelete = function() {
    if (resource && onDelete) {
      onDelete(resource.id);
      onOpenChange(false);
    }
  };

  const getInitials = function(name) {
    if (!name) return '?';
    const parts = name.split(' ');
    let result = '';
    for (let i = 0; i < parts.length && i < 2; i++) {
      if (parts[i][0]) result += parts[i][0];
    }
    return result.toUpperCase();
  };

  const isEditing = resource !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifica Risorsa' : 'Nuova Risorsa'}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica i dettagli della risorsa' : 'Inserisci i dettagli della nuova risorsa'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="flex justify-center mb-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={formData.avatar} alt={formData.name} />
              <AvatarFallback className="text-xl bg-primary/10">
                {getInitials(formData.name)}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resourceName">Nome Risorsa</Label>
            <Input
              id="resourceName"
              value={formData.name}
              onChange={function(e) { setFormData(function(prev) { return { ...prev, name: e.target.value }; }); }}
              placeholder="Es: Mario Rossi"
              data-testid="resource-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="resourceAvatar">URL Avatar (opzionale)</Label>
            <Input
              id="resourceAvatar"
              value={formData.avatar}
              onChange={function(e) { setFormData(function(prev) { return { ...prev, avatar: e.target.value }; }); }}
              placeholder="https://example.com/avatar.jpg"
              data-testid="resource-avatar-input"
            />
            <p className="text-xs text-muted-foreground">
              Inserisci l'URL di un'immagine per l'avatar
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resourceCsr">CSR (EUR / giorno)</Label>
            <Input
              id="resourceCsr"
              type="number"
              min="0"
              step="0.01"
              value={formData.csr}
              onChange={function(e) { setFormData(function(prev) { return { ...prev, csr: e.target.value }; }); }}
              placeholder="Es: 450"
              data-testid="resource-csr-input"
            />
          </div>

          <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
            {isEditing && onDelete && (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDelete}
                className="sm:mr-auto"
                data-testid="delete-resource-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </Button>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={function() { onOpenChange(false); }}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={!formData.name}
                data-testid="save-resource-btn"
              >
                {isEditing ? 'Salva Modifiche' : 'Crea Risorsa'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceDialog;
